// src/pages/KDS/components/KDSStationFilter.jsx
import React, { useState, useRef, useEffect } from 'react';
import { ChefHat, Utensils, Coffee, Pizza, Flame, ChevronDown, Check } from 'lucide-react';

// Icon mapping for common station types
const STATION_ICONS = {
  'main': ChefHat,
  'kitchen': ChefHat,
  'tandoor': Flame,
  'grill': Flame,
  'bar': Coffee,
  'beverages': Coffee,
  'drinks': Coffee,
  'pizza': Pizza,
  'default': Utensils
};

function getStationIcon(stationName) {
  const nameLower = stationName?.toLowerCase() || '';
  for (const [key, Icon] of Object.entries(STATION_ICONS)) {
    if (nameLower.includes(key)) return Icon;
  }
  return STATION_ICONS.default;
}

export default function KDSStationFilter({ stations = [], selectedStations = [], onStationChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allSelected = selectedStations.length === 0 || selectedStations.includes('all');

  const handleStationToggle = (stationId) => {
    if (stationId === 'all') {
      onStationChange(['all']);
      return;
    }

    let newSelection;
    if (allSelected) {
      // If "all" was selected, switch to just this station
      newSelection = [stationId];
    } else if (selectedStations.includes(stationId)) {
      // Remove station from selection
      newSelection = selectedStations.filter(id => id !== stationId);
      // If no stations selected, default to all
      if (newSelection.length === 0) {
        newSelection = ['all'];
      }
    } else {
      // Add station to selection
      newSelection = [...selectedStations, stationId];
    }
    onStationChange(newSelection);
  };

  const getDisplayText = () => {
    if (allSelected) return 'All Stations';
    if (selectedStations.length === 1) {
      const station = stations.find(s => s.id?.toString() === selectedStations[0]);
      return station?.name || 'Station';
    }
    return `${selectedStations.length} Stations`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-sm font-medium text-white transition-colors"
      >
        <ChefHat size={16} />
        <span>{getDisplayText()}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-slate-700 border border-slate-600 rounded-lg shadow-lg z-50 py-1">
          {/* All Stations Option */}
          <button
            onClick={() => handleStationToggle('all')}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-600 transition-colors"
          >
            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
              allSelected ? 'bg-teal-500 border-teal-500' : 'border-slate-500'
            }`}>
              {allSelected && <Check size={12} className="text-white" />}
            </div>
            <ChefHat size={16} className="text-slate-400" />
            <span className="text-white">All Stations</span>
          </button>

          <div className="border-t border-slate-600 my-1" />

          {/* Individual Station Options */}
          {stations.map(station => {
            const Icon = getStationIcon(station.name);
            const isSelected = !allSelected && selectedStations.includes(station.id?.toString());

            return (
              <button
                key={station.id}
                onClick={() => handleStationToggle(station.id?.toString())}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-600 transition-colors"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  isSelected ? 'bg-teal-500 border-teal-500' : 'border-slate-500'
                }`}>
                  {isSelected && <Check size={12} className="text-white" />}
                </div>
                <Icon size={16} className="text-slate-400" />
                <span className="text-white flex-1 text-left">{station.name}</span>
                {station.orderCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs bg-teal-500/20 text-teal-400">
                    {station.orderCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
